import { defineStore } from 'pinia'
import pointsService from '@/services/points.service'
import type {
  PointTransaction,
  PointConversion,
  Withdrawal,
  Deposit,
  UserStreak,
  UserLevel
} from '@/types/points.types'

interface PointsState {
  transactions: PointTransaction[]
  conversions: PointConversion[]
  withdrawals: Withdrawal[]
  deposits: Deposit[]
  userLevel: UserLevel | null
  streak: UserStreak | null
  totalPoints: number
  totalGainedPoints: number
  totalMoneyAmount: number
  moneyAmount: number
  loading: boolean
}

export const usePointsStore = defineStore('points', {
  state: (): PointsState => ({
    transactions: [],
    conversions: [],
    withdrawals: [],
    deposits: [],
    userLevel: null,
    streak: null,
    totalPoints: 0,
    totalGainedPoints: 0,
    totalMoneyAmount: 0,
    moneyAmount: 0,
    loading: false
  }),

  getters: {
    availablePoints: (state) => state.totalPoints,
    
    pendingConversions: (state) => {
      return state.conversions.filter(c => c.status === 'pending')
    },

    pendingWithdrawals: (state) => {
      return state.withdrawals.filter(w => w.status === 'pending')
    },

    totalEarned: (state) => {
      return state.transactions
        .filter(t => t.points > 0)
        .reduce((sum, t) => sum + t.points, 0)
    },

    totalSpent: (state) => {
      return Math.abs(state.transactions
        .filter(t => t.points < 0)
        .reduce((sum, t) => sum + t.points, 0))
    }
  },

  actions: {
    async fetchPointHistory(userId?: number) {
      this.loading = true
      try {
        this.transactions = await pointsService.getPointHistory(userId)
      } finally {
        this.loading = false
      }
    },

    async fetchUserPoints(userId?: number) {
      const data = await pointsService.getUserPoints(userId)
      this.totalPoints = data.points
      this.totalGainedPoints = data.total_gained_points ?? 0
      this.totalMoneyAmount = data.total_money_amount ?? 0
      this.moneyAmount = data.money_amount ?? 0
    },

    async fetchConversionHistory() {
      this.loading = true
      try {
        this.conversions = await pointsService.getConversionHistory()
      } finally {
        this.loading = false
      }
    },

    async fetchWithdrawals() {
      this.loading = true
      try {
        this.withdrawals = await pointsService.getWithdrawals()
      } finally {
        this.loading = false
      }
    },

    async fetchDeposits() {
      this.loading = true
      try {
        this.deposits = await pointsService.getDeposits()
      } finally {
        this.loading = false
      }
    },

    async convertPoints(points: number) {
      const conversion = await pointsService.convertPoints(points)
      this.conversions.unshift(conversion)
      await this.fetchUserPoints()
      return conversion
    },

    async requestWithdrawal(amount: number, bankDetails: string, notes?: string) {
      const withdrawal = await pointsService.requestWithdrawal(amount, bankDetails, notes)
      this.withdrawals.unshift(withdrawal)
      return withdrawal
    },

    async fetchUserLevel(userId?: number) {
      this.userLevel = await pointsService.getUserLevel(userId)
    },

    async fetchStreakInfo(userId?: number) {
      this.streak = await pointsService.getStreakInfo(userId)
    },

    async initialize(userId?: number) {
      await Promise.all([
        this.fetchUserPoints(userId),
        this.fetchPointHistory(userId),
        this.fetchConversionHistory(),
        this.fetchWithdrawals(),
        this.fetchUserLevel(userId),
        this.fetchStreakInfo(userId)
      ])
    }
  }
})
